import React, { useMemo, useState } from 'react';
import { Download, Edit3, Eye, Plus, Save } from 'lucide-react';
import { cleanPhone, dateLabel, money, saveAdminStudent } from '../lib/compat.js';
import { exportToExcel } from './exports.js';
import { AdminFilterBar, AdminModal, EmptyState, Pager, orderPhone, orderTotal, usePagedRows } from './shared.jsx';

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();
}

export default function AdminStudents({ data, query, onRefresh }) {
  const [filters, setFilters] = useState({ district: '', state: '', source: '' });
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const needle = query.trim().toLowerCase();

  const districts = useMemo(() => [...new Set(data.students.map((s) => s.district).filter(Boolean))].sort(), [data.students]);
  const states = useMemo(() => [...new Set(data.students.map((s) => s.state).filter(Boolean))].sort(), [data.students]);

  const rows = useMemo(() => data.students.filter((s) => {
    const text = [s.name, s.phone, s.id, s.email, s.district, s.state].join(' ').toLowerCase();
    return (!needle || text.includes(needle)) &&
      (!filters.district || s.district === filters.district) &&
      (!filters.state || s.state === filters.state) &&
      (!filters.source || s.source === filters.source);
  }), [data.students, needle, filters]);

  const pager = usePagedRows(rows, 20);

  function getOrderCount(student) {
    const phone = cleanPhone(student.phone || student.id);
    return data.orders.filter((o) => {
      const op = orderPhone(o);
      return op === phone || cleanPhone(o.studentPhone) === phone;
    }).length;
  }

  function doExport() {
    exportToExcel('muppadai-students', rows.map((s) => ({
      Name: s.name || '',
      Phone: s.phone || s.id || '',
      Email: s.email || '',
      'Alt Phone': s.altPhone || '',
      Street: s.street || '',
      District: s.district || '',
      State: s.state || '',
      Pincode: s.pincode || '',
      DOB: s.dob || '',
      Source: s.source || '',
      Address: s.address || ''
    })), 'Students');
  }

  return (
    <>
      <AdminFilterBar>
        <div className="filterGroup">
          <label>District</label>
          <select value={filters.district} onChange={(e) => setFilters({ ...filters, district: e.target.value })}>
            <option value="">All Districts</option>
            {districts.map((d) => <option value={d} key={d}>{d}</option>)}
          </select>
        </div>
        <div className="filterGroup">
          <label>State</label>
          <select value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}>
            <option value="">All States</option>
            {states.map((s) => <option value={s} key={s}>{s}</option>)}
          </select>
        </div>
        <div className="filterGroup">
          <label>Source</label>
          <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
            <option value="">All</option>
            <option value="online">Online Orders</option>
            <option value="manual_entry">Manual Entry</option>
            <option value="admin_added">Admin Added</option>
          </select>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" onClick={() => setFilters({ district: '', state: '', source: '' })}>Clear</button>
          <button type="button" className="btn-green" onClick={() => setEditing({})}><Plus size={14} /> Add Student</button>
          <button type="button" className="btn-export-xl" onClick={doExport}><Download size={14} /> Excel</button>
        </div>
      </AdminFilterBar>

      <div className="adminCard">
        <div className="adminCardHead">
          <h3>Students</h3>
          <span style={{ fontSize: 12, color: 'var(--admin-muted)' }}>Showing {pager.paged.length} of {rows.length}</span>
        </div>
        <div className="tableWrap">
          <table>
            <thead>
              <tr>
                <th>Student</th>
                <th>Phone</th>
                <th>Email</th>
                <th>Location</th>
                <th>DOB</th>
                <th>Orders</th>
                <th>Source</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pager.paged.map((student) => {
                const phone = cleanPhone(student.phone || student.id);
                const orderCount = getOrderCount(student);
                return (
                  <tr key={student.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="studentAvatar">{initials(student.name)}</div>
                        <div>
                          <strong>{student.name || 'Customer'}</strong>
                          <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{student.id}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {phone}
                      {student.altPhone && <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{student.altPhone}</div>}
                    </td>
                    <td style={{ fontSize: 12 }}>{student.email || '—'}</td>
                    <td style={{ fontSize: 12 }}>
                      <strong>{student.district || '—'}</strong>
                      {student.state && <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{student.state}</div>}
                      {student.pincode && <div style={{ fontSize: 11, color: 'var(--admin-muted)' }}>{student.pincode}</div>}
                    </td>
                    <td style={{ fontSize: 12 }}>{student.dob || '—'}</td>
                    <td>
                      <strong>{orderCount}</strong>
                    </td>
                    <td>
                      <span className="badge" style={{ background: student.source === 'online' ? '#E8F5EE' : '#EEF0FF', color: student.source === 'online' ? '#1a7a3e' : '#2F338C', fontSize: 10 }}>
                        {student.source || 'unknown'}
                      </span>
                    </td>
                    <td>
                      <div className="rowActions">
                        <button type="button" onClick={() => setViewing(student)}><Eye size={13} /> View</button>
                        <button type="button" onClick={() => setEditing(student)}><Edit3 size={13} /> Edit</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rows.length && <EmptyState title="No students found" body="Try adjusting your filters." />}
        </div>
        <Pager state={pager} total={rows.length} />
      </div>

      {editing !== null && (
        <StudentFormModal
          student={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await onRefresh(); }}
        />
      )}
      {viewing && (
        <StudentViewModal
          student={viewing}
          orders={data.orders}
          onClose={() => setViewing(null)}
          onEdit={() => { setViewing(null); setEditing(viewing); }}
        />
      )}
    </>
  );
}

function StudentFormModal({ student, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: student.name || '',
    phone: cleanPhone(student.phone || student.id || ''),
    altPhone: student.altPhone || '',
    email: student.email || '',
    street: student.street || '',
    postOffice: student.postOffice || '',
    pincode: student.pincode || '',
    district: student.district || '',
    state: student.state || 'Tamil Nadu',
    dob: student.dob || '',
    source: student.source || 'admin_added',
    id: student.id
  });
  const [msg, setMsg] = useState('');

  async function save() {
    try {
      await saveAdminStudent(form);
      await onSaved();
    } catch (e) {
      setMsg(e.message || 'Unable to save student.');
    }
  }

  return (
    <AdminModal title={student.id ? 'Edit Student' : 'Add Student'} onClose={onClose}>
      {msg && <div className="banner danger compact">{msg}</div>}
      <div className="adminFormGrid">
        <label>Name *<input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
        <label>Phone *<input value={form.phone} maxLength={10} disabled={!!student.id} onChange={(e) => setForm({ ...form, phone: cleanPhone(e.target.value) })} /></label>
        <label>Alt Phone<input value={form.altPhone} maxLength={10} onChange={(e) => setForm({ ...form, altPhone: cleanPhone(e.target.value) })} /></label>
        <label>Email<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
        <label className="full">Street / Door No / Area<input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} /></label>
        <label>Post Office<input value={form.postOffice} onChange={(e) => setForm({ ...form, postOffice: e.target.value })} /></label>
        <label>Pincode<input value={form.pincode} maxLength={6} onChange={(e) => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })} /></label>
        <label>District<input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} /></label>
        <label>State<input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></label>
        <label>Date of Birth<input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} /></label>
        <label>Source
          <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
            <option value="admin_added">Admin Added</option>
            <option value="online">Online</option>
            <option value="manual_entry">Manual Entry</option>
          </select>
        </label>
      </div>
      <div className="modalActions">
        <button type="button" onClick={onClose}>Cancel</button>
        <button type="button" className="btn-primary" onClick={save}><Save size={14} /> Save</button>
      </div>
    </AdminModal>
  );
}

function StudentViewModal({ student, orders, onClose, onEdit }) {
  const phone = cleanPhone(student.phone || student.id);
  const studentOrders = orders.filter((o) => {
    const op = orderPhone(o);
    return op === phone || cleanPhone(o.studentPhone) === phone;
  });
  const totalSpent = studentOrders.reduce((s, o) => s + Number(o.total || orderTotal(o) || 0), 0);

  return (
    <AdminModal title="Student Profile" onClose={onClose} wide>
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 16 }}>
        <div className="studentAvatar" style={{ width: 56, height: 56, fontSize: 22 }}>{(student.name || '?')[0].toUpperCase()}</div>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--admin-navy)', margin: 0 }}>{student.name || 'Customer'}</h2>
          <p style={{ fontSize: 13, color: 'var(--admin-muted)', margin: '2px 0' }}>+91 {phone}</p>
          {student.email && <p style={{ fontSize: 12, color: 'var(--admin-muted)', margin: 0 }}>{student.email}</p>}
        </div>
        <button type="button" className="btn-primary" style={{ marginLeft: 'auto' }} onClick={onEdit}><Edit3 size={14} /> Edit</button>
      </div>
      <div className="detailGrid">
        <div><span>Orders</span><strong>{studentOrders.length}</strong></div>
        <div><span>Total Spent</span><strong>{money(totalSpent)}</strong></div>
        <div><span>District</span><strong>{student.district || '—'}</strong></div>
        <div><span>State</span><strong>{student.state || '—'}</strong></div>
        {student.pincode && <div><span>Pincode</span><strong>{student.pincode}</strong></div>}
        {student.dob && <div><span>DOB</span><strong>{student.dob}</strong></div>}
        <div><span>Source</span><strong>{student.source || '—'}</strong></div>
      </div>
      <div className="modalSection">
        <h4>Address</h4>
        <p style={{ fontSize: 13 }}>{student.address || [student.street, student.postOffice, student.district, student.state].filter(Boolean).join(', ') + (student.pincode ? ' — ' + student.pincode : '') || '—'}</p>
      </div>
      <div className="modalSection">
        <h4>Recent Orders</h4>
        {studentOrders.length ? (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr><th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Order</th><th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Date</th><th style={{ textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Total</th><th style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid var(--admin-border)' }}>Status</th></tr></thead>
            <tbody>
              {studentOrders.slice(0, 5).map((o) => (
                <tr key={o.id}>
                  <td style={{ padding: '4px 6px' }}>{o.orderId || o.id}</td>
                  <td style={{ padding: '4px 6px', color: 'var(--admin-muted)' }}>{dateLabel(o.createdAt)}</td>
                  <td style={{ padding: '4px 6px', textAlign: 'right' }}>{money(Number(o.total || orderTotal(o) || 0))}</td>
                  <td style={{ padding: '4px 6px' }}>{o.deliveryStatus || o.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : <p style={{ fontSize: 13, color: 'var(--admin-muted)' }}>No orders yet.</p>}
      </div>
    </AdminModal>
  );
}
